import React, { useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Button } from '@garnish/core';

/**
 * Continue button to be used in the bottom of the bag.
 */
export const BagContinueToCheckoutCta = (props: BagContinueToCheckoutCtaProps) => {
  const { accessibilityLabel, cta, isDisabled, isLoading, onPress } = props;

  const { formatMessage } = useIntl();

  // ─── Refs ─────────────────────────────────────────────────────────────────

  const ref = useRef(null);

  // ─── Derived ──────────────────────────────────────────────────────────────

  const isDisabledWithoutLoading = isDisabled && !isLoading;

  // ─── Styles ───────────────────────────────────────────────────────────────

  const ctaStyles = [styles.cta, isDisabledWithoutLoading ? styles.ctaDisabled : null];
  const labelStyles = [styles.ctaLabel, isDisabledWithoutLoading ? styles.ctaLabelDisabled : null];

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <Button
        ref={ref}
        size="large"
        palette="primaryLime"
        style={ctaStyles}
        accessibilityLabel={accessibilityLabel ?? formatMessage(messages.continueToCheckout)}
        disabled={isDisabledWithoutLoading}
        isLoading={isLoading}
        onPress={onPress}
      >
        <BodyText style={labelStyles} sizeMatch={['18']}>
          {cta}
        </BodyText>
      </Button>
    </View>
  );
};

// ─── Constants ──────────────────────────────────────────────────────────────

const CTA_HEIGHT = 64;

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  continueToBag: {
    defaultMessage: 'Continue',
    description: 'Bag | Floating CTAs | To Bag',
  },
  continueToCheckout: {
    defaultMessage: 'Continue to checkout',
    description: 'Bag | Floating CTAs | To Checkout',
  },
  viewMenu: {
    defaultMessage: 'Menu',
    description: 'Bag | Floating CTAs | View Menu',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: CTA_HEIGHT,
  },
  cta: {
    ...theme.elevations[6],
    minHeight: CTA_HEIGHT,
  },
  ctaLabel: {
    color: theme.colors.LIME,
  },
  ctaDisabled: {
    backgroundColor: theme.colors.LIGHT,
  },
  ctaLabelDisabled: {
    color: theme.colors.CHARCOAL,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagContinueToCheckoutCtaProps = {
  accessibilityLabel?: string;
  cta: string;
  isDisabled: boolean;
  isLoading?: boolean;
  onPress: () => void;
};
